import type {
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  Room,
} from 'livekit-client'
import './styles.css'
import type {
  BootstrapPayload,
  ChatMessage,
  HostMessage,
  ModalityUpdatePayload,
  SessionPayload,
  WidgetModality,
} from './types'

type ViewState =
  | 'loading'
  | 'welcome'
  | 'connecting'
  | 'text'
  | 'permission'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'reconnecting'
  | 'handoff'
  | 'contact'
  | 'feedback'
  | 'error'

const app = document.querySelector<HTMLDivElement>('#app')!
let hostOrigin = ''
let apiBase = ''
let pageUrl = ''
let pageTitle = ''
let referrerUrl = ''
let bootstrap: BootstrapPayload | null = null
let session: SessionPayload | null = null
let room: Room | null = null
let livekit: typeof import('livekit-client') | null = null
let modality: WidgetModality | null = null
let requestedModality: WidgetModality | null = null
let state: ViewState = 'loading'
let messages: ChatMessage[] = []
let endTimer: number | null = null
let ending = false
let pendingQuestion = ''
let permissionFallbackResolver: (() => void) | null = null
let permissionAttempt = 0
let consentGranted = false
const defaultPrivacyNoticeUrl = 'https://noeticframe.com/privacy'
let handoffMode: 'connected' | 'contact' | null = null
let contactSaved = false
let errorCode = ''
let lastAudioLevel = 0
const modalityAcks = new Map<
  string,
  { resolve: (accepted: boolean) => void; timer: number }
>()

const visitorId = (() => {
  const key = 'rynvio.widget.visitor'
  const created = crypto.randomUUID()
  try {
    const existing = sessionStorage.getItem(key)
    if (existing) return existing
    sessionStorage.setItem(key, created)
  } catch {
    // Storage can be blocked by browser privacy controls; the session still works.
  }
  return created
})()

const operationId = (prefix: string) => `${prefix}-${crypto.randomUUID()}`
const sessionStartId = operationId('widget-session')
const contactSubmissionId = operationId('widget-contact')
const isUsageLimitError = () =>
  [
    'usage_limit_reached',
    'text_reply_limit_reached',
    'audio_usage_limit_reached',
  ].includes(errorCode)
const acquisitionKeys = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
] as const

function acquisitionContext() {
  try {
    const url = new URL(pageUrl)
    const values: Record<string, string> = {}
    for (const key of acquisitionKeys) {
      const value = url.searchParams.get(key)?.trim()
      if (value) values[key] = value.slice(0, 256)
    }
    if (referrerUrl) {
      const referrerHost = new URL(referrerUrl).hostname
      if (referrerHost) values.referrer_host = referrerHost.slice(0, 256)
    }
    return Object.keys(values).length ? values : undefined
  } catch {
    return undefined
  }
}

function parentPost(type: 'rynvio:minimize' | 'rynvio:closed' | 'rynvio:refresh-bootstrap') {
  if (hostOrigin) window.parent.postMessage({ type }, hostOrigin)
}

function setState(next: ViewState) {
  if (state === next) return
  const currentInput = document.querySelector<HTMLInputElement>('#message-input')
  const draft = currentInput?.value || ''
  const restoreInputFocus = document.activeElement === currentInput
  state = next
  render()
  const nextInput = document.querySelector<HTMLInputElement>('#message-input')
  if (nextInput && draft) nextInput.value = draft
  if (nextInput && restoreInputFocus) {
    nextInput.focus()
    nextInput.setSelectionRange(nextInput.value.length, nextInput.value.length)
  }
}

function escapeHtml(value: string) {
  const node = document.createElement('span')
  node.textContent = value
  return node.innerHTML
}

function escapeAttribute(value: string) {
  return escapeHtml(value).replaceAll('"', '&quot;').replaceAll("'", '&#39;')
}

function statusCopy() {
  const copies: Record<ViewState, string> = {
    loading: '準備しています',
    welcome: 'ご相談をお聞かせください',
    connecting: 'AI受付につないでいます',
    text: 'テキストで応対中',
    permission: 'マイクの許可を確認しています',
    listening: 'お話しください',
    thinking: '考えています',
    speaking: 'AI受付が話しています',
    reconnecting: '再接続しています',
    handoff: 'スタッフと接続中',
    contact: contactSaved ? 'お問い合わせを保存しました' : 'ご連絡先をお預かりします',
    feedback: '応対はお役に立ちましたか？',
    error: isUsageLimitError() ? 'AI受付は利用できません' : '接続できませんでした',
  }
  return copies[state]
}

function render() {
  if (!bootstrap) {
    app.innerHTML = '<main class="shell shell--loading"><div class="loading-spinner" aria-hidden="true"></div><p>受付を準備しています…</p></main>'
    return
  }
  document.documentElement.style.setProperty('--terracotta', bootstrap.config.primary_color)
  const config = bootstrap.config
  const resolvedTheme =
    config.theme === 'auto'
      ? typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : config.theme
  document.documentElement.dataset.theme = resolvedTheme
  const isConversation = [
    'text',
    'listening',
    'thinking',
    'speaking',
    'reconnecting',
    'handoff',
    'contact',
  ].includes(state)
  app.innerHTML = `
    <main class="shell" data-state="${state}" role="dialog" aria-modal="true" aria-label="Rynvio AI受付">
      <header class="header">
        <div class="identity">
          <div><strong>${escapeHtml(config.agent_name)}</strong><span>${escapeHtml(config.ai_disclosure)}</span></div>
        </div>
        <button class="icon-button" id="minimize" type="button" aria-label="受付ウィンドウを閉じる" title="閉じる"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 8 5 5 5-5" /></svg></button>
      </header>
      ${state === 'welcome' ? '' : `<div class="status" aria-hidden="true"><i></i><span>${statusCopy()}</span></div>`}
      ${state === 'welcome' ? welcomeView(config) : ''}
      ${state === 'connecting' ? connectingView() : ''}
      ${state === 'permission' ? permissionView() : ''}
      ${isConversation ? conversationView() : ''}
      ${state === 'feedback' ? feedbackView() : ''}
      ${state === 'error' ? errorView() : ''}
      <div class="sr-only" id="announcement" role="status" aria-live="polite" aria-atomic="true"></div>
      <footer class="footer"><a href="https://rynvio.app" target="_blank" rel="noopener noreferrer">Powered by Rynvio</a><span>録音は保存しません</span></footer>
    </main>`
  bindViewEvents()
  if (state === 'welcome') {
    const welcomeFocus =
      document.querySelector<HTMLInputElement>('#consent') ||
      document.querySelector<HTMLButtonElement>('[data-start]')
    welcomeFocus?.focus()
  }
  if (state === 'feedback') {
    document.querySelector<HTMLButtonElement>('[data-feedback]')?.focus()
  }
  if (state === 'error') {
    document.querySelector<HTMLButtonElement>('#retry')?.focus()
  }
  if (isConversation) {
    renderMessages()
    if (state === 'text') document.querySelector<HTMLInputElement>('#message-input')?.focus()
  }
}

function welcomeView(config: BootstrapPayload['config']) {
  const consent = bootstrap?.consent_mode === 'explicit'
  const privacyUrl = bootstrap?.privacy_notice_url || defaultPrivacyNoticeUrl
  const privacy = `<a href="${escapeAttribute(privacyUrl)}" target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>`
  return `
    <section class="welcome">
      <h1>${escapeHtml(config.welcome_message)}</h1>
      ${
        consent
          ? `<label class="consent"><input id="consent" type="checkbox"/><span>${escapeHtml(config.transcription_consent_text)} ${privacy}</span></label>`
          : `<p class="notice">${escapeHtml(config.transcription_consent_text)} ${privacy}</p>`
      }
      <div class="mode-grid${config.text_enabled && config.voice_enabled ? '' : ' mode-grid--single'}">
        ${config.text_enabled ? '<button class="mode mode--text" data-start="text" type="button"><span>文字で相談</span><b aria-hidden="true">→</b></button>' : ''}
        ${config.voice_enabled ? '<button class="mode mode--voice" data-start="voice" type="button"><span>話して相談</span><b aria-hidden="true">→</b></button>' : ''}
      </div>
      ${
        config.text_enabled && config.suggested_questions.length
          ? `<div class="suggestions" aria-label="よくある質問">${config.suggested_questions
              .map(
                (question, index) =>
                  `<button data-question-index="${index}" type="button">${escapeHtml(question)}</button>`,
              )
              .join('')}</div>`
          : ''
      }
      <p class="validation" id="validation" role="alert"></p>
    </section>`
}

function connectingView() {
  return `<section class="connecting"><div class="orbit" aria-hidden="true"><i></i><i></i><i></i></div><h1>受付につないでいます</h1><p>通常は数秒で始まります。</p></section>`
}

function permissionView() {
  return `<section class="permission"><div class="permission__mark" aria-hidden="true">◌</div><h1>マイクの許可を確認します</h1><p>音声で相談するため、このサイトでマイクの利用を許可してください。録音データは保存しません。</p><button id="continue-text" type="button">テキストで続ける</button></section>`
}

function conversationView() {
  const voice = modality === 'voice'
  const showComposer = bootstrap?.config.text_enabled !== false
  const canSwitch = Boolean(
    bootstrap?.config.text_enabled && bootstrap?.config.voice_enabled,
  )
  const switchDisabled = ['thinking', 'speaking', 'reconnecting'].includes(state)
  return `
    <section class="conversation conversation--${voice ? 'voice' : 'text'}">
      ${
        voice
          ? `<div class="voice-stage"><div class="wave" aria-hidden="true">${'<i></i>'.repeat(9)}</div><strong>${statusCopy()}</strong><span>${showComposer ? '途中でテキスト入力もできます' : '音声でそのままご相談ください'}</span></div>`
          : ''
      }
      <div class="messages" id="messages" aria-label="会話履歴"></div>
      ${state === 'contact' ? contactView() : showComposer ? `<form class="composer" id="composer">
        <label class="sr-only" for="message-input">メッセージ</label>
        <input id="message-input" autocomplete="off" maxlength="2000" placeholder="質問を入力…" />
        <button type="submit" aria-label="送信">↑</button>
      </form>` : ''}
      ${state === 'contact' ? '' : `<div class="conversation-actions">
        ${voice ? '<button id="mic-toggle" class="quiet-button" type="button">マイクを切る</button>' : ''}
        ${state !== 'handoff' && canSwitch ? `<button id="switch-mode" class="quiet-button" type="button" ${switchDisabled ? 'disabled' : ''}>${voice ? 'テキストに切り替える' : '音声に切り替える'}</button>` : ''}
        <button id="end" class="quiet-button" type="button">応対を終了</button>
      </div>`}
    </section>`
}

function contactView() {
  if (contactSaved) {
    return `<section class="contact-success"><strong>送信しました</strong><p>担当者が内容を確認します。</p><button id="close-final" type="button">閉じる</button></section>`
  }
  const consentText =
    bootstrap?.config.contact_consent_text ||
    '入力した連絡先を担当者への連絡に利用することに同意します。'
  return `<form class="contact-form" id="contact-form">
    <label>お名前<input name="name" maxlength="255" autocomplete="name" /></label>
    <div class="contact-form__split">
      <label>メール<input name="email" maxlength="320" type="email" autocomplete="email" /></label>
      <label>電話番号（任意）<input name="phone" maxlength="32" type="tel" autocomplete="tel" /></label>
    </div>
    <label>ご希望の時間（任意）<input name="preferred_time" maxlength="255" /></label>
    <label>メッセージ<textarea name="message" maxlength="5000" required></textarea></label>
    <label class="contact-consent"><input name="consent" type="checkbox" /><span>${escapeHtml(consentText)}</span></label>
    <p class="validation" id="contact-validation" role="alert"></p>
    <button class="contact-submit" type="submit">担当者へ送る</button>
  </form>`
}

function feedbackView() {
  return `<section class="feedback"><p class="eyebrow">THANK YOU</p><h1>応対はお役に立ちましたか？</h1><div><button data-feedback="true" type="button">はい</button><button data-feedback="false" type="button">いいえ</button></div><button id="close-final" class="text-button" type="button">閉じる</button></section>`
}

function errorView() {
  if (isUsageLimitError()) {
    const title = session
      ? 'AI受付を終了しました'
      : '現在、AI受付は利用できません'
    return `<section class="error"><div aria-hidden="true">!</div><h1>${title}</h1><p>恐れ入りますが、ほかのお問い合わせ方法をご利用ください。</p><button id="close-final" type="button">閉じる</button></section>`
  }
  return `<section class="error"><div aria-hidden="true">!</div><h1>うまく接続できませんでした</h1><p>少し時間をおいて、もう一度お試しください。</p><button id="retry" type="button">もう一度試す</button></section>`
}

function bindViewEvents() {
  document.querySelector('#minimize')?.addEventListener('click', () => void minimize())
  document.querySelectorAll<HTMLButtonElement>('[data-start]').forEach((button) => {
    button.addEventListener('click', () => void startSession(button.dataset.start as WidgetModality))
  })
  document.querySelectorAll<HTMLButtonElement>('[data-question-index]').forEach((button) => {
    button.addEventListener('click', () => {
      const index = Number(button.dataset.questionIndex)
      pendingQuestion = bootstrap?.config.suggested_questions[index] || ''
      void startSession('text')
    })
  })
  document.querySelector('#composer')?.addEventListener('submit', (event) => {
    event.preventDefault()
    void sendMessage()
  })
  document.querySelector('#contact-form')?.addEventListener('submit', (event) => {
    event.preventDefault()
    void submitContact(event.currentTarget as HTMLFormElement)
  })
  document.querySelector('#end')?.addEventListener('click', () => void endSession())
  document.querySelector('#mic-toggle')?.addEventListener('click', () => void toggleMicrophone())
  document.querySelector('#switch-mode')?.addEventListener('click', () => {
    void switchModality(modality === 'voice' ? 'text' : 'voice').catch(handleError)
  })
  document.querySelector('#retry')?.addEventListener('click', () => void retrySession())
  document.querySelector('#continue-text')?.addEventListener('click', () => permissionFallbackResolver?.())
  document.querySelector('#close-final')?.addEventListener('click', () => parentPost('rynvio:closed'))
  document.querySelectorAll<HTMLButtonElement>('[data-feedback]').forEach((button) => {
    button.addEventListener('click', () => void submitFeedback(button.dataset.feedback === 'true'))
  })
}

function isAgentParticipant(participant: { identity: string }) {
  return !participant.identity.startsWith('visitor_') && !participant.identity.startsWith('operator_')
}

async function waitForAgentParticipant(activeRoom: Room, timeoutMs = 10_000) {
  if ([...activeRoom.remoteParticipants.values()].some(isAgentParticipant)) return
  await new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      activeRoom.off(livekit!.RoomEvent.ParticipantConnected, onParticipantConnected)
      reject(new Error('agent_unavailable'))
    }, timeoutMs)
    const onParticipantConnected = (participant: RemoteParticipant) => {
      if (!isAgentParticipant(participant)) return
      window.clearTimeout(timer)
      activeRoom.off(livekit!.RoomEvent.ParticipantConnected, onParticipantConnected)
      resolve()
    }
    activeRoom.on(livekit!.RoomEvent.ParticipantConnected, onParticipantConnected)
  })
}

function updateWaveform(level: number) {
  lastAudioLevel = Math.max(0, Math.min(1, level))
  const gains = [0.62, 0.88, 1.16, 0.82, 1.32, 0.96, 1.18, 0.76, 0.58]
  document.querySelectorAll<HTMLElement>('.wave i').forEach((bar, index) => {
    const scale = Math.min(1, 0.2 + lastAudioLevel * 2.8 * (gains[index] || 1))
    bar.style.transform = `scaleY(${scale.toFixed(3)})`
    bar.style.opacity = String(Math.min(1, 0.48 + lastAudioLevel * 2.2))
  })
}

async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${apiBase}${path}`, {
    ...init,
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      ...(session ? { Authorization: `Bearer ${session.session_token}` } : {}),
      ...init.headers,
    },
  })
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { detail?: { code?: string } } | null
    throw new Error(payload?.detail?.code || 'request_failed')
  }
  return response.json() as Promise<T>
}

async function startSession(selected: WidgetModality) {
  if (!bootstrap || ['connecting', 'permission'].includes(state)) return
  errorCode = ''
  if (
    bootstrap.consent_mode === 'explicit' &&
    !consentGranted &&
    !document.querySelector<HTMLInputElement>('#consent')?.checked
  ) {
    const validation = document.querySelector<HTMLElement>('#validation')
    if (validation) validation.textContent = '内容をご確認のうえ、同意してください。'
    return
  }
  consentGranted = true
  if (selected === 'voice') pendingQuestion = ''
  requestedModality = selected
  if (selected === 'voice') {
    const currentPermissionAttempt = ++permissionAttempt
    modality = 'voice'
    setState('permission')
    const browserPermission: Promise<WidgetModality> = navigator.mediaDevices?.getUserMedia
      ? navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then((stream) => {
            stream.getTracks().forEach((track) => track.stop())
            return 'voice' as const
          })
          .catch(() => 'text' as const)
      : Promise.resolve('text')
    const manualFallback = new Promise<WidgetModality>((resolve) => {
      permissionFallbackResolver = () => resolve('text')
    })
    selected = await Promise.race([browserPermission, manualFallback])
    permissionFallbackResolver = null
    if (currentPermissionAttempt !== permissionAttempt) return
    if (selected === 'text') {
      requestedModality = 'text'
      messages.push({
        id: crypto.randomUUID(),
        role: 'system',
        text: 'マイクを利用できないため、テキストで応対を始めます。',
      })
    }
  }
  modality = selected
  setState('connecting')
  try {
    session = await apiRequest<SessionPayload>('/v1/widget/sessions', {
      method: 'POST',
      headers: { 'Idempotency-Key': sessionStartId },
      body: JSON.stringify({
        embed_token: bootstrap.embed_token,
        modality: selected,
        consent_granted: true,
        visitor_id: visitorId,
        page_url: pageUrl,
        page_title: pageTitle,
        referrer_url: referrerUrl,
        locale: navigator.language,
        acquisition_context: acquisitionContext(),
      }),
    })
    await connectRoom(session)
    if (selected === 'text' && pendingQuestion) {
      const input = document.querySelector<HTMLInputElement>('#message-input')
      if (input) input.value = pendingQuestion
      pendingQuestion = ''
      await sendMessage()
    }
    window.clearTimeout(endTimer ?? undefined)
    endTimer = window.setTimeout(() => void endSession('idle_timeout'), session.max_ai_session_seconds * 1000)
  } catch (error) {
    handleError(error)
  }
}

async function connectRoom(credentials: SessionPayload) {
  room?.disconnect()
  livekit ??= await import('livekit-client')
  const { Room, RoomEvent, Track } = livekit
  room = new Room({ adaptiveStream: true, dynacast: true })
  let agentReady = false
  let resolveAgentReady: (() => void) | null = null
  const agentReadySignal = new Promise<void>((resolve) => {
    resolveAgentReady = resolve
  })
  room.registerTextStreamHandler('lk.chat', (reader, participant) => {
    if (participant.identity.startsWith('visitor_')) return
    void reader.readAll().then((text) => {
      if (text.trim()) {
        appendMessage(
          participant.identity.startsWith('operator_') ? 'operator' : 'assistant',
          text.trim(),
          reader.info.id,
        )
      }
    })
  })
  room.registerTextStreamHandler('rynvio.handoff', (reader, participant) => {
    if (participant.identity.startsWith('visitor_')) return
    void reader.readAll().then((raw) => {
      try {
        const payload = JSON.parse(raw) as { mode?: 'connected' | 'contact' }
        if (payload.mode === 'connected') {
          handoffMode = 'connected'
          setState('handoff')
        } else if (payload.mode === 'contact') {
          handoffMode = 'contact'
          void room?.localParticipant.setMicrophoneEnabled(false).catch(() => undefined)
          setState('contact')
        }
      } catch {
        // Ignore malformed Agent control messages.
      }
    })
  })
  room.registerTextStreamHandler('rynvio.modality.ack', (reader, participant) => {
    if (participant.identity.startsWith('visitor_')) return
    void reader.readAll().then((raw) => {
      try {
        const payload = JSON.parse(raw) as {
          transition_id?: string
          accepted?: boolean
        }
        const pending = payload.transition_id
          ? modalityAcks.get(payload.transition_id)
          : undefined
        if (!pending || !payload.transition_id) return
        window.clearTimeout(pending.timer)
        modalityAcks.delete(payload.transition_id)
        pending.resolve(payload.accepted === true)
      } catch {
        // Ignore malformed control data; the bounded timeout owns recovery.
      }
    })
  })
  room.registerTextStreamHandler('rynvio.ready', (reader, participant) => {
    if (!isAgentParticipant(participant)) return
    void reader.readAll().then(() => {
      agentReady = true
      resolveAgentReady?.()
    })
  })
  room.on(
    RoomEvent.TrackSubscribed,
    (track: RemoteTrack, _publication: RemoteTrackPublication, _participant: RemoteParticipant) => {
      if (track.kind === Track.Kind.Audio) {
        if (modality !== 'voice') return
        const element = track.attach()
        element.dataset.rynvioAudio = 'true'
        document.body.append(element)
      }
    },
  )
  room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => track.detach().forEach((node) => node.remove()))
  room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
    if (handoffMode === 'connected') return
    if (modality !== 'voice' || state === 'permission') return
    const agentSpeaker = speakers.find((speaker) => isAgentParticipant(speaker))
    const visitorSpeaker = speakers.find((speaker) => speaker.identity.startsWith('visitor_'))
    const agentSpeaking = Boolean(agentSpeaker)
    setState(agentSpeaking ? 'speaking' : 'listening')
    updateWaveform((agentSpeaker || visitorSpeaker)?.audioLevel || 0)
  })
  room.on(RoomEvent.Reconnecting, () => setState('reconnecting'))
  room.on(RoomEvent.Reconnected, () =>
    setState(handoffMode === 'connected' ? 'handoff' : modality === 'voice' ? 'listening' : 'text'),
  )
  room.on(RoomEvent.Disconnected, () => {
    if (!ending && !['feedback', 'error'].includes(state)) {
      void handleRoomDisconnected()
    }
  })
  room.on(RoomEvent.ParticipantDisconnected, (participant) => {
    if (isAgentParticipant(participant) && !ending && !['feedback', 'error'].includes(state)) {
      handleError(new Error('agent_unavailable'))
    }
  })
  await room.connect(credentials.realtime.server_url, credentials.realtime.token, {
    autoSubscribe: true,
  })
  await waitForAgentParticipant(room)
  if (!agentReady) {
    await Promise.race([
      agentReadySignal,
      new Promise<never>((_, reject) => {
        window.setTimeout(() => reject(new Error('agent_unavailable')), 15_000)
      }),
    ])
  }
  if (modality === 'voice') {
    setState('permission')
    try {
      await room.localParticipant.setMicrophoneEnabled(true)
      setState('listening')
      updateWaveform(0)
    } catch {
      await continueWithTextAfterMicFailure()
    }
  } else {
    setState('text')
  }
}

async function continueWithTextAfterMicFailure() {
  document.querySelectorAll<HTMLMediaElement>('[data-rynvio-audio]').forEach((element) => {
    element.pause()
    element.remove()
  })
  messages.push({
    id: crypto.randomUUID(),
    role: 'system',
    text: 'マイクを利用できないため、テキストで応対を続けます。',
  })
  try {
    await switchModality('text', { skipRender: true })
  } catch (error) {
    handleError(error)
  }
}

async function retrySession() {
  errorCode = ''
  if (!session) {
    if (requestedModality) {
      await startSession(requestedModality)
    } else {
      modality = null
      setState('welcome')
    }
    return
  }
  setState('connecting')
  try {
    session = await apiRequest<SessionPayload>(
      `/v1/widget/sessions/${session.conversation_id}/reconnect`,
      { method: 'POST' },
    )
    modality = session.modality
    await connectRoom(session)
    await syncAgentModality(session.modality)
    window.clearTimeout(endTimer ?? undefined)
    endTimer = window.setTimeout(
      () => void endSession('idle_timeout'),
      session.max_ai_session_seconds * 1000,
    )
  } catch (error) {
    handleError(error)
  }
}

async function handleRoomDisconnected() {
  if (!session || ending || state === 'feedback') return
  const terminal = new Set(['completed', 'abandoned', 'failed'])
  try {
    for (let attempt = 0; attempt < 12; attempt += 1) {
      const status = await apiRequest<{ status: string; close_reason?: string | null }>(
        `/v1/widget/sessions/${session.conversation_id}`,
      )
      if (terminal.has(status.status)) {
        if (
          status.close_reason &&
          ['text_reply_limit_reached', 'audio_usage_limit_reached'].includes(
            status.close_reason,
          )
        ) {
          errorCode = status.close_reason
          setState('error')
          return
        }
        setState('feedback')
        return
      }
      if (!['ending', 'processing'].includes(status.status)) break
      await new Promise((resolve) => window.setTimeout(resolve, 250))
    }
  } catch {
    // A genuine network failure cannot query server state and remains retryable.
  }
  errorCode = 'request_failed'
  setState('error')
}

function appendMessage(
  role: ChatMessage['role'],
  text: string,
  id: string = crypto.randomUUID(),
) {
  if (messages.some((message) => message.id === id)) return
  messages.push({ id, role, text })
  messages = messages.slice(-50)
  renderMessages()
  if (role === 'assistant' || role === 'operator') {
    const announcement = document.querySelector<HTMLElement>('#announcement')
    if (announcement) announcement.textContent = text
  }
}

function renderMessages() {
  const container = document.querySelector<HTMLElement>('#messages')
  if (!container) return
  container.innerHTML = messages
    .map(
      (message) =>
        `<article class="message message--${message.role}"><span>${message.role === 'visitor' ? 'あなた' : message.role === 'assistant' ? 'AI受付' : message.role === 'operator' ? 'スタッフ' : 'ご案内'}</span><p>${escapeHtml(message.text)}</p></article>`,
    )
    .join('')
  container.scrollTop = container.scrollHeight
}

async function sendMessage() {
  const input = document.querySelector<HTMLInputElement>('#message-input')
  const text = input?.value.trim()
  if (!text || !room || room.state !== livekit?.ConnectionState.Connected) return
  if (input) input.value = ''
  appendMessage('visitor', text)
  if (modality === 'voice' && handoffMode !== 'connected') setState('thinking')
  try {
    await room.localParticipant.sendText(text, { topic: 'lk.chat' })
  } catch (error) {
    handleError(error)
  }
}

async function submitContact(form: HTMLFormElement) {
  if (!session || contactSaved) return
  const data = new FormData(form)
  const email = String(data.get('email') || '').trim()
  const phone = String(data.get('phone') || '').trim()
  const message = String(data.get('message') || '').trim()
  const consent = data.get('consent') === 'on'
  const validation = document.querySelector<HTMLElement>('#contact-validation')
  if ((!email && !phone) || !message || !consent) {
    if (validation) {
      validation.textContent = 'メールまたは電話番号、メッセージ、同意を確認してください。'
    }
    return
  }
  const button = form.querySelector<HTMLButtonElement>('.contact-submit')
  if (button) button.disabled = true
  try {
    await apiRequest(`/v1/widget/sessions/${session.conversation_id}/contact`, {
      method: 'POST',
      headers: { 'Idempotency-Key': contactSubmissionId },
      body: JSON.stringify({
        name: String(data.get('name') || '').trim() || null,
        email: email || null,
        phone: phone || null,
        preferred_time: String(data.get('preferred_time') || '').trim() || null,
        message,
        consent,
      }),
    })
    contactSaved = true
    render()
  } catch {
    if (button) button.disabled = false
    if (validation) validation.textContent = '保存できませんでした。もう一度お試しください。'
  }
}

async function toggleMicrophone() {
  if (!room) return
  const enabled = room.localParticipant.isMicrophoneEnabled
  try {
    await room.localParticipant.setMicrophoneEnabled(!enabled)
  } catch {
    await continueWithTextAfterMicFailure()
    return
  }
  const button = document.querySelector<HTMLButtonElement>('#mic-toggle')
  if (button) button.textContent = enabled ? 'マイクを入れる' : 'マイクを切る'
}

function waitForModalityAck(transitionId: string): Promise<boolean> {
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      modalityAcks.delete(transitionId)
      resolve(false)
    }, 8_000)
    modalityAcks.set(transitionId, { resolve, timer })
  })
}

async function syncAgentModality(
  target: WidgetModality,
  transitionId: string = operationId('modality-sync'),
) {
  if (!room) throw new Error('realtime_unavailable')
  const ack = waitForModalityAck(transitionId)
  await room.localParticipant.sendText(
    JSON.stringify({ modality: target, transition_id: transitionId }),
    { topic: 'rynvio.modality' },
  )
  if (!(await ack)) throw new Error('modality_transition_timeout')
}

async function requestServerModality(
  target: WidgetModality,
  transitionId: string,
) {
  if (!session) throw new Error('session_unavailable')
  return apiRequest<ModalityUpdatePayload>(
    `/v1/widget/sessions/${session.conversation_id}/modality`,
    {
      method: 'POST',
      headers: { 'Idempotency-Key': `widget-${transitionId}` },
      body: JSON.stringify({ modality: target, transition_id: transitionId }),
    },
  )
}

async function compensateModality(previous: WidgetModality) {
  const rollbackId = operationId('modality-rollback')
  try {
    const reverted = await requestServerModality(previous, rollbackId)
    if (!reverted.accepted || reverted.modality !== previous) return false
    await syncAgentModality(previous, rollbackId)
    return true
  } catch {
    return false
  }
}

async function terminateInconsistentModality() {
  if (!session) return
  try {
    await apiRequest(`/v1/widget/sessions/${session.conversation_id}/end`, {
      method: 'POST',
      headers: {
        'Idempotency-Key': `widget-modality-failed-${session.conversation_id}`,
      },
      body: JSON.stringify({ reason: 'modality_transition_failed' }),
      keepalive: true,
    })
  } catch {
    // The server reaper remains the final fallback for an interrupted request.
  }
}

function removeRemoteAudio() {
  document.querySelectorAll<HTMLMediaElement>('[data-rynvio-audio]').forEach((element) => {
    element.pause()
    element.remove()
  })
}

function attachCurrentRemoteAudio() {
  if (!room || !livekit) return
  for (const participant of room.remoteParticipants.values()) {
    for (const publication of participant.trackPublications.values()) {
      const track = publication.track
      if (!track || track.kind !== livekit.Track.Kind.Audio) continue
      for (const existing of track.detach()) existing.remove()
      const element = track.attach()
      element.dataset.rynvioAudio = 'true'
      document.body.append(element)
    }
  }
}

async function switchModality(
  target: WidgetModality,
  options: { skipRender?: boolean } = {},
) {
  if (!session || !room || !modality || target === modality) {
    if (target === modality && !options.skipRender) {
      setState(target === 'voice' ? 'listening' : 'text')
    }
    return
  }
  if (target === 'voice') {
    if (!bootstrap?.config.voice_enabled) throw new Error('modality_not_available')
    if (!navigator.mediaDevices?.getUserMedia) throw new Error('microphone_unavailable')
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    stream.getTracks().forEach((track) => track.stop())
  } else if (!bootstrap?.config.text_enabled) {
    throw new Error('modality_not_available')
  }

  const previous = modality
  const transitionId = operationId('modality')
  if (!options.skipRender) setState('connecting')
  const updated = await requestServerModality(target, transitionId)
  if (!updated.accepted || updated.modality !== target) {
    throw new Error('modality_transition_rejected')
  }
  try {
    await syncAgentModality(target, transitionId)
  } catch (error) {
    if (await compensateModality(previous)) {
      messages.push({
        id: crypto.randomUUID(),
        role: 'system',
        text: '切り替えを完了できなかったため、元の応対方法に戻しました。',
      })
      setState(previous === 'voice' ? 'listening' : 'text')
      return
    }
    await terminateInconsistentModality()
    throw error
  }

  if (target === 'voice') {
    modality = 'voice'
    session.modality = 'voice'
    requestedModality = 'voice'
    attachCurrentRemoteAudio()
    try {
      await room.localParticipant.setMicrophoneEnabled(true)
    } catch {
      await continueWithTextAfterMicFailure()
      return
    }
    setState('listening')
  } else {
    try {
      await room.localParticipant.setMicrophoneEnabled(false)
    } catch {
      // The server already revoked microphone publishing.
    }
    modality = 'text'
    session.modality = 'text'
    requestedModality = 'text'
    removeRemoteAudio()
    setState('text')
  }
}

async function endSession(reason = 'visitor_ended') {
  if (!session || ending) {
    setState('feedback')
    return
  }
  ending = true
  window.clearTimeout(endTimer ?? undefined)
  try {
    await apiRequest(`/v1/widget/sessions/${session.conversation_id}/end`, {
      method: 'POST',
      headers: { 'Idempotency-Key': `widget-end-${session.conversation_id}` },
      body: JSON.stringify({ reason }),
      keepalive: true,
    })
  } catch {
    // Local disconnect still ends media; the backend reaper owns recovery.
  }
  room?.disconnect()
  ending = false
  setState('feedback')
}

async function minimize() {
  if (!session && state === 'permission') {
    permissionAttempt += 1
    permissionFallbackResolver?.()
    permissionFallbackResolver = null
    requestedModality = null
    modality = null
    setState('welcome')
  }
  parentPost('rynvio:minimize')
}

async function submitFeedback(resolved: boolean) {
  if (session) {
    try {
      await apiRequest(`/v1/widget/sessions/${session.conversation_id}/feedback`, {
        method: 'POST',
        headers: { 'Idempotency-Key': `widget-feedback-${session.conversation_id}` },
        body: JSON.stringify({ resolved }),
      })
    } catch {
      // Feedback is optional and must never trap the visitor in the iframe.
    }
  }
  parentPost('rynvio:closed')
}

function handleError(error: unknown) {
  const code = error instanceof Error ? error.message : 'request_failed'
  errorCode = code
  const recoverable = new Set(['realtime_unavailable', 'agent_unavailable', 'request_failed'])
  if (!recoverable.has(code)) messages = []
  setState('error')
  room?.disconnect()
  if (
    !session &&
    ['invalid_embed_token', 'embed_token_used', 'deployment_changed'].includes(code)
  ) {
    parentPost('rynvio:refresh-bootstrap')
  }
}

window.addEventListener('message', (event: MessageEvent<HostMessage>) => {
  if (event.source !== window.parent) return
  if (event.data?.type === 'rynvio:context') {
    if (!bootstrap || event.origin !== hostOrigin) return
    try {
      if (new URL(event.data.pageUrl).origin !== hostOrigin) return
    } catch {
      return
    }
    pageUrl = event.data.pageUrl
    pageTitle = event.data.pageTitle.slice(0, 512)
    referrerUrl = event.data.referrerUrl
    return
  }
  if (!['rynvio:bootstrap', 'rynvio:bootstrap-refresh'].includes(event.data?.type || '')) return
  if (event.data.hostOrigin !== event.origin) return
  const isRefresh = event.data.type === 'rynvio:bootstrap-refresh'
  if ((!isRefresh && bootstrap) || (isRefresh && session)) return
  try {
    if (new URL(event.data.bootstrap.widget_origin).origin !== window.location.origin) return
  } catch {
    return
  }
  hostOrigin = event.origin
  apiBase = event.data.apiBase.replace(/\/$/, '')
  pageUrl = event.data.pageUrl
  pageTitle = event.data.pageTitle
  referrerUrl = event.data.referrerUrl
  bootstrap = event.data.bootstrap
  document.documentElement.lang = bootstrap.default_locale
  if (isRefresh && requestedModality) {
    void retrySession()
  } else {
    setState('welcome')
  }
})

window.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    event.preventDefault()
    void minimize()
  }
})

window.addEventListener('pagehide', (event) => {
  if (event.persisted || !session || ending || state === 'feedback') return
  ending = true
  void apiRequest(`/v1/widget/sessions/${session.conversation_id}/end`, {
    method: 'POST',
    headers: { 'Idempotency-Key': `widget-pagehide-${session.conversation_id}` },
    body: JSON.stringify({ reason: 'page_unloaded' }),
    keepalive: true,
  }).catch(() => undefined)
})

render()
